import type { SessionByeOptions } from 'sip.js';
import type { PooledDialerMemberView } from '../../../shared/types/pooled-dialer';

// Mid-call rescue, client side (users.pool_call_rescue). A reload drops the
// tab's WebRTC session; the server holds the prospect and rings the new tab.
// Two things only the browser can supply:
//  - which BYEs were the agent pressing hang-up (a dying tab's BYE looks the
//    same on the wire), and
//  - on Android the dying tab often sends no BYE at all, so the reloaded tab
//    has to say "that live call is mine, and I am back" itself.

// Read by the backend as sip_bye_h_X-Sales-Hangup on the agent leg's hangup.
const AGENT_HANGUP_HEADER = 'X-Sales-Hangup: agent';

export function byeOptionsFor(intent: 'agent' | null): SessionByeOptions | undefined {
  return intent === 'agent' ? { requestOptions: { extraHeaders: [AGENT_HANGUP_HEADER] } } : undefined;
}

// Per tab: sessionStorage survives the reload of this tab and nothing else.
export const LIVE_CALL_STORAGE_KEY = 'pool.liveCall';

export interface PoolLiveCallMarker {
  campaignId: string;
  contactLegId: string;
}

type Membership = { campaignId: string; member: PooledDialerMemberView } | null;

export function parseLiveCallMarker(raw: string | null): PoolLiveCallMarker | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<PoolLiveCallMarker> | null;
    return typeof value?.campaignId === 'string' && typeof value.contactLegId === 'string'
      ? { campaignId: value.campaignId, contactLegId: value.contactLegId }
      : null;
  } catch {
    return null;
  }
}

function liveCallOf(membership: Membership): PoolLiveCallMarker | null {
  const member = membership?.member;
  if (!membership || member?.state !== 'busy' || !member.currentLegId || member.inbound) return null;
  return { campaignId: membership.campaignId, contactLegId: member.currentLegId };
}

/**
 * What the owner tab does with its marker for this snapshot. Written while a
 * flagged bridged pool call is live; cleared only once the server shows that
 * call over — never on unload, which is exactly when it must survive. A
 * missing membership is a loading page or a delivery gap, not an ended call.
 */
export function liveCallMarkerStep(input: {
  membership: Membership;
  hasRuntimeLease: boolean;
  marker: PoolLiveCallMarker | null;
}): { kind: 'write'; marker: PoolLiveCallMarker } | { kind: 'clear' } | { kind: 'keep' } {
  if (!input.membership) return { kind: 'keep' };
  const live = liveCallOf(input.membership);
  if (live && input.membership.member.callRescue && input.hasRuntimeLease) {
    const same = input.marker?.campaignId === live.campaignId && input.marker.contactLegId === live.contactLegId;
    return same ? { kind: 'keep' } : { kind: 'write', marker: live };
  }
  if (input.marker && input.marker.contactLegId !== live?.contactLegId) return { kind: 'clear' };
  return { kind: 'keep' };
}

/**
 * The reloaded tab may claim the call it carried across the reload — never
 * once a pool line is established here: the call is already back, and a
 * claim would end the leg carrying it.
 */
export function claimReady(input: {
  carried: PoolLiveCallMarker | null;
  membership: Membership;
  hasRuntimeLease: boolean;
  phoneOnline: boolean;
  poolLineLive: boolean;
}): boolean {
  const live = liveCallOf(input.membership);
  return Boolean(
    input.carried
    && input.hasRuntimeLease
    && input.phoneOnline
    && !input.poolLineLive
    && live
    && live.campaignId === input.carried.campaignId
    && live.contactLegId === input.carried.contactLegId
  );
}

/** The carried call is over: nothing left to claim. Null membership = unknown yet. */
export function carriedCallEnded(carried: PoolLiveCallMarker, membership: Membership): boolean {
  if (!membership) return false;
  return liveCallOf(membership)?.contactLegId !== carried.contactLegId;
}

export function rescueSecondsLeft(rescueDeadline: string | null, nowMs: number): number | null {
  if (!rescueDeadline) return null;
  const deadline = Date.parse(rescueDeadline);
  if (Number.isNaN(deadline)) return null;
  return Math.max(0, Math.ceil((deadline - nowMs) / 1000));
}

export interface RescueWatch {
  legId: string;
  startedAt: number;
}

/**
 * One telemetry event per rescue this tab saw. `now` is monotonic
 * (performance.now()) — agent wall clocks drift here.
 */
export function rescueWatchStep(
  watch: RescueWatch | null,
  member: PooledDialerMemberView | null,
  now: number
): { watch: RescueWatch | null; event: { name: string; payload: Record<string, unknown> } | null } {
  if (!member) return { watch, event: null };
  const onCall = member.state === 'busy' && member.currentLegId !== null;
  if (!watch) {
    return member.rescueDeadline && onCall && member.currentLegId
      ? { watch: { legId: member.currentLegId, startedAt: now }, event: null }
      : { watch: null, event: null };
  }
  if (onCall && member.currentLegId === watch.legId) {
    if (member.rescueDeadline) return { watch, event: null };
    return {
      watch: null,
      event: { name: 'reload_call_rescued', payload: { legId: watch.legId, gapMs: Math.round(now - watch.startedAt) } },
    };
  }
  return {
    watch: null,
    event: { name: 'reload_call_lost', payload: { legId: watch.legId, reason: 'call_ended', state: member.state } },
  };
}
